import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  KeyboardAvoidingView,
  Keyboard,
} from 'react-native';
import db from '../../lib/db';
import heroStore from '../../stores/hero';
import { confirm } from '../shared/Alert';
import { debounce, uniqBy } from 'lodash';
import { mapObjToArray } from '../../utils/utils';
import { GOLDEN, GOLDEN300, pOrT } from '../styleConstants';
import Image from '../shared/Image';
import appStore from '../../stores/app';
import { PaperBackground } from '../Island/IslandScreen';
import { TextInputLabel } from '../shared/TextInput';
import IconButton from '../shared/IconButton';
import Text from '../shared/Text';
import warriorInfoModalStore from '../common/WarriorInfoModal/store';
import { t } from '../../utils/appUtils';
import moment from 'moment';
import { isAdmin } from '../../utils/warriorUtils';
import { getWarriorByLogin } from '../../lib/dbCalls';
import { Message, createMessage, pickWarriorData } from './utils';
import { IS_WEB } from '../../lib/appConstants';
import { observe } from 'mobx';

const INITIAL_CHAT_MSGS_COUNT = 50;

let Draggable: any = View;
if (IS_WEB) {
  Draggable = (window as any).Draggable;
}

const Chat = ({ id, visible, onHide }: { id: string; visible: boolean; onHide: Function }) => {
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [channel, setChannel] = useState<'world' | 'clan'>('world');

  const clanId = heroStore.hero.clanId;
  const channelPath = channel === 'clan' && clanId ? `clanChat/${clanId}` : 'chat';
  const [position, setPosition] = useState({
    x: appStore.chatState.position.x,
    y: appStore.chatState.position.y,
  });
  const { height, width } = useWindowDimensions();
  const disposer = useRef<any>(null);
  const [hoveredClose, setHoveredClose] = useState(false);

  const scrollViewRef = useRef<any>(null);
  const scrolling = useRef(false);

  const createMessageAction = async () => {
    Keyboard.dismiss();

    let message: Partial<Message> = {
      message: value,
      warrior: pickWarriorData(heroStore.hero) as any,
    };

    if (!message.message) return;

    message.private = value.startsWith('prv:');
    if (message.private || value.startsWith('to:')) {
      const login = value.split(' ')[0].replace('prv:', '').replace('to:', '');
      message.message = value.split(' ').slice(1).join(' ');

      if (!message.message) return;

      const warrior = await getWarriorByLogin(login);
      if (!warrior) {
        appStore.showMessage({ type: 'error', text: t('warriorNotFound') });
        return;
      }

      // @ts-ignore
      message.to = pickWarriorData(warrior);
    }

    createMessage(message, channelPath);
    setValue('');
    scrollToEnd();
    appStore.chatState.scrollOffset = -1;
  };

  const deleteMessage = async (message: Message) => {
    confirm(renderMessage(message), async () => {
      await db().child(channelPath).child(message.timestamp).remove();
    });
  };

  const scrollToEnd = (animated = false) => {
    setTimeout(() => {
      scrolling.current = true;

      if (scrollViewRef.current) scrollViewRef.current.scrollToEnd({ animated });
      setTimeout(() => {
        scrolling.current = false;
      }, 300);
    }, 100);
  };

  const listenToMessages = (path: string) => {
    const addedQuery = db().child(path).endAt().limitToLast(1);
    const addedCb = (snapshot: any) => {
      const message = snapshot.val();

      setMessages((messages) => uniqBy([...messages, message], 'timestamp'));

      if (appStore.chatState.scrollOffset === -1) {
        scrollToEnd(true);
      }
    };
    addedQuery.on('child_added', addedCb);

    const removedRef = db().child(path);
    const removedCb = (snapshot: any) => {
      const message = snapshot.val();

      setMessages((messages) => messages.filter((item) => item.timestamp !== message.timestamp));
    };
    removedRef.on('child_removed', removedCb);

    return () => {
      addedQuery.off('child_added', addedCb);
      removedRef.off('child_removed', removedCb);
    };
  };

  const fetchMessages = async (path: string) => {
    const val = await db().child(path).endAt().limitToLast(INITIAL_CHAT_MSGS_COUNT).once('value');
    if (!val.exists()) {
      setMessages([]);
      return;
    }
    setMessages(mapObjToArray(val.val()) as Message[]);
  };

  const renderMessage = (message: Message) => {
    return message.messageKey
      ? t(`chat.sysMessages.${message.messageKey}`, message.messageData)
      : message.message;
  };

  const scrollTo = () => {
    if (appStore.chat) {
      if (appStore.chatState.scrollOffset !== -1) {
        if (IS_WEB) {
          scrollViewRef.current.scrollTo({ y: appStore.chatState.scrollOffset, animated: true });
        } else {
          setTimeout(() => {
            scrollViewRef.current.scrollTo({ y: appStore.chatState.scrollOffset, animated: true });
          }, 500);
        }
      } else {
        scrollToEnd();
      }
    }
  };

  useEffect(() => {
    disposer.current = observe(appStore, 'chatState', () => {
      if (IS_WEB)
        setPosition({ x: appStore.chatState.position.x, y: appStore.chatState.position.y });
      debounce(() => {
        if (appStore.chatState.activeChat === 'combat' && id === 'inner') scrollTo();
      }, 100);
    });
    appStore.updateChatState({ activeChat: id });
    return () => {
      appStore.updateChatState({ activeChat: 'inner' });
      disposer.current();
    };
  }, []);

  // (Re)subscribe whenever the active channel changes.
  useEffect(() => {
    setMessages([]);
    let cancelled = false;
    fetchMessages(channelPath).then(() => {
      if (!cancelled) scrollTo();
    });
    const cleanup = listenToMessages(channelPath);
    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelPath]);

  useEffect(() => {
    scrollTo();
  }, [visible]);

  const size = {
    width: 520,
    height: 340,
  };

  const style: any = {
    position: 'absolute',
    display: !visible ? 'none' : undefined,
    width: size.width,
    height: size.height,
    zIndex: 99999,
  };

  return (
    <Draggable
      position={{ x: position.x, y: position.y }}
      onStop={(_: any, data: any) => {
        setPosition({ x: data.x, y: data.y });
        appStore.updateChatState({ position: { x: data.x, y: data.y } });
      }}
      style={
        IS_WEB
          ? undefined
          : {
              ...style,
              left: 80,
              top: 30,
            }
      }
    >
      <View style={IS_WEB ? style : undefined}>
        <PaperBackground
          width={size.width}
          height={size.height}
          style={{
            marginLeft: 0,
            marginTop: 0,
          }}
        />

        <TouchableOpacity
          onPress={() => {
            Keyboard.dismiss();
            onHide();
          }}
          style={{
            right: 0,
            top: 0,
            width: pOrT(24, 28),
            height: pOrT(24, 28),
            position: 'absolute',
            zIndex: 9,
          }}
          // @ts-ignore
          onMouseEnter={() => {
            setHoveredClose(true);
          }}
          onMouseLeave={() => {
            setHoveredClose(false);
          }}
        >
          <Image
            style={{
              width: pOrT(24, 28),
              height: pOrT(24, 28),
            }}
            source={
              hoveredClose
                ? require('../../assets/images/closeOn.png')
                : require('../../assets/images/close.png')
            }
          />
        </TouchableOpacity>

        <View style={{ height: size.height, overflow: 'hidden', marginTop: 25 }}>
          <KeyboardAvoidingView behavior="position" keyboardVerticalOffset={25}>
            <View style={{ paddingHorizontal: 25, paddingBottom: 25 }}>              
              <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                <TouchableOpacity onPress={() => setChannel('world')} style={{ marginRight: 14 }}>
                  <Text bold style={{ color: channel === 'world' ? GOLDEN300 : GOLDEN }}>
                    {t('clan.chatWorld')}
                  </Text>
                </TouchableOpacity>
                {clanId ? <TouchableOpacity onPress={() => setChannel('clan')}>
                  <Text bold style={{ color: channel === 'clan' ? GOLDEN300 : GOLDEN }}>
                    {t('clan.chatClan')}
                  </Text>
                </TouchableOpacity> : null}
              </View>              
              <ScrollView
                onScroll={(e) => {
                  if (scrolling.current) return;
                  appStore.updateChatState({ scrollOffset: e.nativeEvent.contentOffset.y });
                }}
                ref={scrollViewRef}
                style={{ height: 230 }}
              >
                {(messages as any[])
                  .filter((item: any) => {
                    if (
                      item.private &&
                      item.to.id !== heroStore.hero.id &&
                      item.warrior.id !== heroStore.hero.id
                    )
                      return false;
                    return true;
                  })
                  .map((message: any) => (
                    <View
                      key={message.timestamp}
                      style={{
                        borderRadius: 10,
                        minWidth: 150,
                        maxWidth: 280,
                        marginTop: 10,
                        paddingHorizontal: 10,
                        paddingVertical: 10,
                        alignSelf:
                          message.warrior.id === heroStore.hero.id ? 'flex-end' : 'flex-start',
                        backgroundColor: message.system
                          ? '#970000'
                          : message.private
                          ? '#944c4c'
                          : message.warrior.id === heroStore.hero.id
                          ? '#894624'
                          : '#772b2b',
                      }}
                    >
                      <View style={{ flexDirection: 'row' }}>
                        <View style={{ flexDirection: 'row' }}>
                          <Text bold style={{ fontSize: 12, color: '#f1d8c7' }}>
                            {message.warrior.login} [{message.warrior.level}]
                          </Text>
                          <IconButton
                            style={{ marginLeft: 5 }}
                            iconStyle={{
                              width: 14,
                              height: 14,
                            }}
                            source={require('../../assets/images/info.png')}
                            onPress={() => warriorInfoModalStore.toggle(message.warrior.id)}
                            size={12}
                            name="info"
                            title={t('hero.info')}
                          />
                        </View>
                        {!message.system && message.to ? (
                          <View style={{ flexDirection: 'row' }}>
                            <Text bold>{message.private ? ' private to ' : ' to '}</Text>
                            <Text bold style={{ fontSize: 12, color: '#f1d8c7' }}>
                              {`${message.to.login} [${message.to.level}]`}
                            </Text>
                            <IconButton
                              style={{ marginLeft: 5 }}
                              iconStyle={{
                                width: 14,
                                height: 14,
                              }}
                              source={require('../../assets/images/info.png')}
                              onPress={() => warriorInfoModalStore.toggle(message.to[0].id)}
                              size={12}
                              name="info"
                              title={t('hero.info')}
                            />
                          </View>
                        ) : null}
                      </View>
                      <Text
                        style={{ marginTop: 3, color: '#eccbb4', fontSize: 14 }}
                        key={message.timestamp}
                      >
                        {renderMessage(message)}
                      </Text>
                      <Text bold style={{ alignSelf: 'flex-end', fontSize: 10 }}>
                        {moment(message.timestamp).fromNow()}
                      </Text>
                      {isAdmin(heroStore.hero) ? (
                        <TouchableOpacity
                          onPress={() => deleteMessage(message)}
                          style={{ position: 'absolute', top: 5, right: 5, zIndex: 9999 }}
                        >
                          <Text style={{ fontSize: 10 }}>Del</Text>
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  ))}
              </ScrollView>

              <View
                style={{
                  flexDirection: 'row',
                  marginTop: 5,
                }}
              >
                <TextInputLabel
                  // @ts-ignore
                  value={value}
                  long
                  maxLength={500}
                  onChangeText={(val: string) => {
                    setValue(val);
                  }}
                  width={433}
                  onSubmitEditing={() => createMessageAction()}
                  returnKeyType="send"
                />
                <IconButton
                  onPress={() => {}}
                  size={20}
                  iconStyle={{ width: 37, height: 37 }}
                  name="sendMessage"
                  // @ts-ignore
                  onPress={() => createMessageAction()}
                  source={require('../../assets/images/send.png')}
                />
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </View>
    </Draggable>
  );
};

Chat.displayName = 'Chat';

export default Chat;
